import React from "react"
import { graphql } from "gatsby"

import Layout from "§/src/components/layout"
import Breadcrumb from "§/src/components/atoms/breadcrumb/breadcrumb-category-sub"

import SchemaBreadcrumb from "§/src/components/schema/s-breadcrumb"
import SchemaProductCategoryChild from "§/src/components/schema/s-product-category-child"

import Error from "§/src/components/error"
import SEO from "§/src/components/seo"
import LoadingFrame from "§/src/components/atoms/loading-frame"

// Functions
import { isEmpty } from "lodash"

// GraphQl Patterns
const { PfsContent } = require("./fragment-pfs")

// Internals
import "./_child.scss"
const ProdCatList = React.lazy(() => import("./prodcatlist"))

// Components
const BoxCustomerCare = React.lazy(() =>
  import("§/src/components/sections/box-customer-care")
)

// Language
import { useTranslation } from "react-i18next"
import { getFormattedLanguage } from "§/src/utils/functions"

export function Head(props) {
  const seo = props?.pageContext?.seo || {}
  const { t } = useTranslation()

  const subcatName = props.pageContext.name
  const subcatDescription = props.pageContext.description
  const catProducts = props.data.allWpProductCategory.nodes[0].products.nodes

  return (
    <React.Fragment>
      <html lang={getFormattedLanguage(process.env.GATSBY_SITE_LANG)}></html>
      <body className="plp-child"></body>
      <SEO
        seo={seo}
        translations={props?.data?.lang?.translations}
        title={subcatName}
        desc={subcatDescription}
        langReplace="products"
      />
      <SchemaBreadcrumb breadcrumbs={seo?.breadcrumbs} />
      <SchemaProductCategoryChild
        subCat={{name: subcatName, description: subcatDescription}}
        catProducts={catProducts}
      />
    </React.Fragment>
  )
}

// Language
const ChildProductCategory = props => {
  const { t } = useTranslation()

  const subCat = props.pageContext
  const subCatAcf = props.data.allWpProductCategory.nodes[0].ctxProductCategory
  const catProducts = props.data.allWpProductCategory.nodes[0].products.nodes
  const subcatName = subCat.name
  const maincatName = subCat.wpParent.node.name
  const maincatUri = subCat.wpParent.node.link
  const customerCareBoxLink =
    props.data.customerCareBox?.acfoptGlobalOptions?.acfGlobal?.productsOptions
      ?.customerCareBoxLink

  return (
    <Layout lang={props?.data?.lang?.translations} langReplace="products">
      <Breadcrumb
        wpseo={props?.pageContext?.seo?.breadcrumbs}
        className=""
        subcatName={subcatName}
        maincatName={maincatName}
        maincatUri={maincatUri}
      />

      { props ? (
        <React.Suspense fallback={<LoadingFrame />}>
          <div id="subcat-title">
            <div className="text-container">
              <h1 className="title h2" dangerouslySetInnerHTML={{ __html: subCat?.name }} />
              {subCat?.subtitle && (
                <div className="subtitle h4" dangerouslySetInnerHTML={{ __html: subCat.subtitle }} />
              )}
              {subCat?.description && (
                <div className="readmore" dangerouslySetInnerHTML={{ __html: subCat.description }} />
              )}
            </div>
            <BoxCustomerCare
              className="d-none d-md-block"
              to={customerCareBoxLink}
            />
          </div>

          {catProducts && (
            <ProdCatList
              id="product-list"
              className="padded"
              catName={subCat?.name}
              data={catProducts}
            />
          )}
          <BoxCustomerCare
            className="d-block d-md-none mt-5"
            to={customerCareBoxLink}
          />
        </React.Suspense>
      ) : (
        <Error message={"Something Went Wrong"} />
      )}
    </Layout>
  )
}

export default ChildProductCategory

export const pageQuery = graphql`
  query GET_SUB_CATEGORIES_PRODUCTS($eq: String!) {
    allWpProductCategory(filter: { slug: { eq: $eq } }) {
      nodes {
        ctxProductCategory {
          subtitle
        }
        products {
          nodes {
            uri
            title
            menuOrder
            acf: AcfCptProduct {
              badge
              configuratorLink
              subTitle
              originalId
            }
            featuredImage {
              node {
                gatsbyImage(
                  layout: CONSTRAINED
                  width: 700
                  fit: CONTAIN
                  cropFocus: CENTER
                  formats: [AUTO, WEBP]
                )
              }
            }
            pfsAccuracy {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsApplication {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsCertificationNorms {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsCharacteristic {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsCurrentRate {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsExCertification {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsFeatures {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsFieldbus {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsFormatMounting {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsHeatsink {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsLoopController {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsMeasuredVariable {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsMechanicalCharacteristic {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsMounting {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsPhase {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsSafetyConformityCert {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsSignalOutput {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsStroke {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsType {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsTypeControl {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsVariable {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsSeries {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsInputOutput {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsSystemAndAccessories {
              nodes {
                name
                slug
                taxonomyName
              }
            }
            pfsFunctionalModules {
              nodes {
                name
                slug
                taxonomyName
              }
            }
          }
        }
      }
    }
    customerCareBox: wp {
      acfoptGlobalOptions {
        acfGlobal {
          productsOptions {
            customerCareBoxLink
          }
        }
      }
    }
    lang: wpProductCategory(slug: { eq: $eq }) {
      translations {
        postUrl
        siteId
      }
    }
  }
`
